import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { SITE } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", address: "", service: "", message: "", website: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Estimate request received. We'll reach out within one business day.");
      setForm(initial);
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + SITE.phone + ".");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="theme-light relative bg-[#F4F5F6] py-24 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left column */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4A84B]" />
            <span className="es-label text-[#D4A84B]">Free for Most Homes</span>
          </div>
          <h2 className="font-serif-display text-[#101010] text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Let's get you on
            <br />
            the schedule.
          </h2>
          <p className="mt-6 text-[#4B5055] text-base sm:text-lg leading-relaxed max-w-md">
            Tell us a little about your roof. We'll come out, walk it ourselves, and send
            a clear written estimate — usually within 48 hours.
          </p>

          <div className="mt-12 space-y-6">
            <ContactRow
              icon={<Phone className="w-5 h-5 text-[#D4A84B]" />}
              label="Phone"
              value={SITE.phone}
              href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`}
              testId="contact-info-phone"
            />
            <ContactRow
              icon={<Mail className="w-5 h-5 text-[#D4A84B]" />}
              label="Email"
              value={SITE.email}
              href={`mailto:${SITE.email}`}
              testId="contact-info-email"
            />
            <ContactRow
              icon={<MapPin className="w-5 h-5 text-[#D4A84B]" />}
              label="Proudly Serving Southeast Wisconsin"
              value="Milwaukee, Waukesha, Racine, Kenosha, Ozaukee, Washington County, Lake Geneva — and surrounding areas."
              testId="contact-info-area"
            />
          </div>

          <div className="mt-12 pt-8 border-t border-[#E5E7EB]">
            <p className="es-label text-[#6B7280]">Office Hours</p>
            <p className="text-[#101010] mt-3 text-sm">Monday – Friday · 7:00 AM – 8:00 PM</p>
            <p className="text-[#101010] text-sm">Saturday · 8:00 AM – 4:00 PM</p>
            <p className="text-[#101010] text-sm">Sunday · By Appointment Only</p>
            <p className="text-[#D4A84B] text-sm mt-3 font-semibold">Emergency tarping available</p>
          </div>
        </div>

        {/* Right column - form card */}
        <div id="estimate" className="lg:col-span-7 scroll-mt-28">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="bg-white border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,16,16,0.04),0_12px_32px_-12px_rgba(16,16,16,0.12)] p-8 lg:p-12"
            noValidate
          >
            <div className="mb-8 flex items-start justify-between gap-6">
              <div>
                <h3 className="font-serif-display text-[#101010] text-2xl sm:text-3xl leading-tight">
                  Request Your Free Estimate
                </h3>
                <p className="mt-2 text-[#4B5055] text-sm">
                  Response under 1 business hour during business hours.
                </p>
              </div>
              <span className="hidden sm:inline-block es-label text-[#D4A84B] bg-[#FFFBEB] border border-[#D4A84B]/30 px-2.5 py-1 whitespace-nowrap">
                No obligation
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
              <Field label="Full Name *">
                <input
                  data-testid="contact-input-name"
                  className="es-input"
                  placeholder="First and last name"
                  value={form.name}
                  onChange={update("name")}
                  disabled={submitting}
                  aria-label="Full name"
                  required
                />
              </Field>
              <Field label="Email *">
                <input
                  data-testid="contact-input-email"
                  className="es-input"
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={update("email")}
                  disabled={submitting}
                  aria-label="Email"
                  required
                />
              </Field>
              <Field label="Phone">
                <input
                  data-testid="contact-input-phone"
                  className="es-input"
                  type="tel"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={update("phone")}
                  disabled={submitting}
                  aria-label="Phone"
                />
              </Field>
              <Field label="Property Address">
                <input
                  data-testid="contact-input-address"
                  className="es-input"
                  placeholder="Street, city"
                  value={form.address}
                  onChange={update("address")}
                  disabled={submitting}
                  aria-label="Property address"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Service Interest">
                  <select
                    data-testid="contact-input-service"
                    className="es-input"
                    value={form.service}
                    onChange={update("service")}
                    disabled={submitting}
                    aria-label="Service interest"
                  >
                    <option value="">Select a service (optional)</option>
                    <option value="Roof Replacement">Roof Replacement</option>
                    <option value="Storm & Hail Damage">Storm & Hail Damage</option>
                    <option value="Gutters & Downspouts">Gutters & Downspouts</option>
                    <option value="Siding">Siding</option>
                    <option value="Soffit & Fascia">Soffit & Fascia</option>
                    <option value="Other / Multiple">Other / Multiple</option>
                  </select>
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Tell us about your project">
                  <textarea
                    data-testid="contact-input-message"
                    className="es-input"
                    rows={4}
                    placeholder="Roof, siding, gutters, storm damage — let us know what you need."
                    value={form.message}
                    onChange={update("message")}
                    disabled={submitting}
                    aria-label="Project details"
                  />
                </Field>
              </div>
              {/* Honeypot */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
              >
                <label>
                  Website (leave this empty)
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                    name="website"
                    data-testid="contact-input-honeypot"
                  />
                </label>
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="text-[#6B7280] text-xs max-w-sm leading-relaxed">
                We respond within one business day. No spam, no aggressive sales calls.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="es-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : done ? (
                  <>Send another <ChevronRight className="w-4 h-4" /></>
                ) : (
                  <>Request Free Estimate <ChevronRight className="w-4 h-4" /></>
                )}
              </button>
              <p
                data-testid="contact-estimate-note"
                className="mt-4 text-[#6B7280] text-xs leading-relaxed"
              >
                Free for most homes. Steep or hard-to-access roofs may require a paid
                inspection — we'll always confirm the cost before visiting.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="es-label text-[#6B7280] block mb-3">{label}</span>
      {children}
    </label>
  );
}

function ContactRow({ icon, label, value, href, testId }) {
  const content = (
    <div className="flex items-start gap-4">
      <span className="mt-1">{icon}</span>
      <div>
        <span className="es-label text-[#6B7280]">{label}</span>
        <p className="text-[#101010] text-base mt-1">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block hover:opacity-80 transition-opacity">
      {content}
    </a>
  ) : (
    <div data-testid={testId}>{content}</div>
  );
}
